import { api } from "@/lib/api";

// Error codes returned by subscribePush so the UI can show specific dialogs.
export const PUSH_ERR = {
  UNSUPPORTED: "PUSH_UNSUPPORTED",   // Browser doesn't support Service Worker / Push API
  DENIED: "PUSH_DENIED",             // Browser previously denied notifications for this site
  DISMISSED: "PUSH_DISMISSED",       // User dismissed the native prompt (state stays "default")
  SW_FAIL: "PUSH_SW_FAIL",           // Service worker registration failed
  VAPID_FAIL: "PUSH_VAPID_FAIL",     // Backend missing VAPID public key
  IOS_NEEDS_INSTALL: "PUSH_IOS_NEEDS_INSTALL", // iOS Safari needs PWA installed to Home Screen
};

function pushError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

// Detects iOS Safari running outside of a home-screen-installed PWA.
// On iOS, web push only works when display-mode is standalone.
function isIosNotInstalled() {
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/.test(ua);
  if (!isIos) return false;
  // standalone mode = launched from home screen
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true;
  return !isStandalone;
}

function urlBase64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  try { return await navigator.serviceWorker.register("/sw.js"); }
  catch (e) { console.warn("SW register failed:", e); return null; }
}

export async function subscribePush() {
  // Capability checks
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    throw pushError(PUSH_ERR.UNSUPPORTED, "Seu navegador não suporta notificações push.");
  }
  if (isIosNotInstalled()) {
    throw pushError(PUSH_ERR.IOS_NEEDS_INSTALL,
      "No iOS, é preciso instalar o app na Tela Inicial primeiro (Safari → Compartilhar → Adicionar à Tela de Início).");
  }

  // Permission flow — check current state BEFORE prompting, so we can give
  // accurate guidance when the browser has already saved a "denied" state
  // (in that case, requestPermission() returns "denied" instantly with no prompt).
  let perm = Notification.permission;
  if (perm === "denied") {
    throw pushError(PUSH_ERR.DENIED,
      "O navegador está bloqueando notificações para este site.");
  }
  if (perm === "default") {
    perm = await Notification.requestPermission();
    if (perm === "denied") {
      throw pushError(PUSH_ERR.DENIED,
        "O navegador está bloqueando notificações para este site.");
    }
    if (perm !== "granted") {
      // User closed/dismissed the prompt without choosing
      throw pushError(PUSH_ERR.DISMISSED,
        "Permissão de notificações não foi concedida. Tente novamente.");
    }
  }

  const reg = (await navigator.serviceWorker.getRegistration()) || (await registerSW());
  if (!reg) throw pushError(PUSH_ERR.SW_FAIL, "Service Worker indisponível.");

  const { data } = await api.get("/notifications/vapid");
  if (!data?.public_key) {
    throw pushError(PUSH_ERR.VAPID_FAIL, "Chave VAPID não configurada no servidor.");
  }

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });
  const subJson = sub.toJSON();
  await api.post("/notifications/subscribe", {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
  });
  return sub;
}

export async function unsubscribePush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.post("/notifications/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys });
    await sub.unsubscribe();
  }
}

export async function isPushEnabled() {
  if (!("serviceWorker" in navigator) || !("Notification" in window)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return !!sub && Notification.permission === "granted";
}

// Returns one of: "granted" | "denied" | "default" | "unsupported"
export function getNotificationPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

// Detect browser family for instruction text
export function detectBrowser() {
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "edge";
  if (/OPR\/|Opera/.test(ua)) return "opera";
  if (/Firefox/.test(ua)) return "firefox";
  if (/Chrome/.test(ua) && !/Edg\//.test(ua)) return "chrome";
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return "safari";
  return "other";
}
